<template>
  <Teleport to="body">
    <template v-if="consent.status.value === 'undecided'">
      <div class="consent-backdrop" />
      <div
        class="consent-banner"
        role="dialog"
        :aria-label="STRINGS.ui.consent.title"
        data-testid="consent-banner"
      >
        <div class="consent-inner">
          <h2 class="consent-title">{{ STRINGS.ui.consent.title }}</h2>
          <p class="consent-body">
            {{ STRINGS.ui.consent.body }}
            <a href="/cookies" class="consent-link" data-testid="consent-learn-more">
              {{ STRINGS.ui.consent.learnMore }}
            </a>
          </p>
          <div class="consent-actions">
            <button
              class="consent-btn consent-btn--accept"
              data-testid="consent-accept"
              @click="consent.accept()"
            >
              {{ STRINGS.ui.consent.accept }}
            </button>
            <button
              class="consent-btn consent-btn--decline"
              data-testid="consent-decline"
              @click="consent.decline()"
            >
              {{ STRINGS.ui.consent.decline }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </Teleport>
</template>

<script setup lang="ts">
import { consent } from '@/telemetry/openpanel';
import STRINGS from '@/constants/strings';
</script>

<style scoped>
.consent-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 12, 8, 0.65);
  z-index: 9000;
  animation: fadeIn 0.4s cubic-bezier(0.39, 0.575, 0.565, 1) both;
}

.consent-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9001;
  padding: 0 1rem 1rem;
  animation: slideUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
}

.consent-inner {
  max-width: 42rem;
  margin: 0 auto;
  background: var(--color-arena-surface, #1a1612);
  border: 1px solid var(--color-arena-border, #3d3428);
  border-radius: var(--radius-card, 1rem);
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
  box-shadow:
    0 -1px 0 0 rgba(201, 168, 76, 0.08) inset,
    0 8px 32px rgba(0, 0, 0, 0.5),
    0 2px 8px rgba(0, 0, 0, 0.3);
}

/* Decorative top accent line */
.consent-inner::before {
  content: '';
  position: absolute;
  top: 0;
  left: 1.5rem;
  right: 1.5rem;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--color-energy-primary, #c9a84c) 20%,
    var(--color-energy-primary, #c9a84c) 80%,
    transparent
  );
  opacity: 0.4;
}

.consent-title {
  font-family: var(--font-display, 'Bricolage Grotesque', system-ui, sans-serif);
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: -0.01em;
  color: var(--color-arena-text-bright, #f5e6c8);
  margin-bottom: 0.75rem;
}

.consent-body {
  font-size: 0.8125rem;
  line-height: 1.6;
  color: var(--color-arena-text-muted, #8a7e6a);
  margin-bottom: 1.25rem;
}

.consent-link {
  color: var(--color-energy-primary, #c9a84c);
  text-decoration: none;
  border-bottom: 1px solid rgba(201, 168, 76, 0.3);
  transition:
    border-color 0.15s ease,
    color 0.15s ease;
}

.consent-link:hover {
  color: var(--color-arena-text-bright, #f5e6c8);
  border-color: var(--color-energy-primary, #c9a84c);
}

.consent-link:focus-visible {
  outline: 2px solid var(--color-energy-primary, #c9a84c);
  outline-offset: 2px;
  border-radius: 2px;
}

.consent-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.consent-btn {
  font-family: var(--font-sans, 'Plus Jakarta Sans', system-ui, sans-serif);
  font-weight: 600;
  font-size: 0.8125rem;
  letter-spacing: 0.01em;
  border: none;
  cursor: pointer;
  border-radius: var(--radius-button, 0.75rem);
  padding: 0.625rem 1.25rem;
  transition:
    transform 0.15s ease,
    filter 0.15s ease,
    box-shadow 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
  line-height: 1;
}

.consent-btn:focus-visible {
  outline: 3px solid var(--color-energy-primary, #c9a84c);
  outline-offset: 2px;
}

.consent-btn--accept {
  background: linear-gradient(
    180deg,
    var(--color-primary-600, #b89636),
    var(--color-energy-primary, #c9a84c)
  );
  color: var(--color-arena-void, #0f0c08);
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.3),
    0 1px 0 rgba(255, 255, 255, 0.1) inset;
}

.consent-btn--accept:hover {
  transform: translateY(-1px);
  filter: brightness(1.1);
  box-shadow:
    0 3px 8px rgba(201, 168, 76, 0.25),
    0 1px 0 rgba(255, 255, 255, 0.1) inset;
}

.consent-btn--accept:active {
  transform: scale(0.98);
  filter: brightness(0.95);
}

.consent-btn--decline {
  background: transparent;
  color: var(--color-arena-text-muted, #8a7e6a);
  border: 1.5px solid var(--color-arena-border, #3d3428);
  padding: calc(0.625rem - 0.75px) calc(1.25rem - 0.75px);
}

.consent-btn--decline:hover {
  color: var(--color-arena-text-bright, #f5e6c8);
  border-color: var(--color-arena-text-dim, #6b5f4e);
  transform: translateY(-1px);
}

.consent-btn--decline:active {
  transform: scale(0.98);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 480px) {
  .consent-banner {
    padding: 0 0.5rem 0.5rem;
  }
  .consent-inner {
    padding: 1.25rem;
    border-radius: 0.75rem;
  }
  .consent-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
  .consent-btn {
    width: 100%;
    text-align: center;
    padding: 0.75rem 1.25rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .consent-banner,
  .consent-backdrop {
    animation: none;
  }
}

@media (prefers-contrast: high) {
  .consent-inner {
    border-color: #ffd700;
  }
  .consent-body {
    color: #fff;
  }
  .consent-link {
    color: #ffd700;
  }
  .consent-btn--accept {
    background: #ffd700;
    color: #000;
  }
  .consent-btn--decline {
    border-color: #fff;
    color: #fff;
  }
}
</style>
